import i18n from "./i18n";

// O'zbekcha oy nomlari (Intl `uz-UZ` ko'p muhitda "M06" kabi chiqaradi —
// shuning uchun qo'lda formatlaymiz).
const UZ_MONTHS = [
  "yanvar", "fevral", "mart", "aprel", "may", "iyun",
  "iyul", "avgust", "sentabr", "oktabr", "noyabr", "dekabr",
];

// Barcha vaqtlar O'zbekiston vaqtida ko'rsatiladi (server UTC'da saqlaydi).
export const TZ = "Asia/Tashkent";

/**
 * Backenddan kelgan vaqtni Date'ga aylantiradi. Agar ISO satrida timezone
 * belgisi bo'lmasa (naive), uni UTC deb hisoblaymiz — server vaqti UTC.
 */
export function parseServerDate(iso: string): Date {
  if (iso.includes("T") && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso)) {
    return new Date(iso + "Z");
  }
  return new Date(iso);
}

/**
 * Butun summani oddiy bo'sh joy (U+0020) bilan minglikka ajratadi: 1250000 →
 * "1 250 000". Intl `uz-UZ` ajratuvchisi uzilmas bo'sh joy (NBSP) qo'yadi —
 * shuning uchun qo'lda guruhlaymiz (loyiha bo'ylab yagona pul formati).
 */
export function groupInt(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  const neg = n < 0;
  const digits = String(Math.round(Math.abs(n))).replace(
    /\B(?=(\d{3})+(?!\d))/g,
    " ",
  );
  return (neg ? "-" : "") + digits;
}

export function fmtMoney(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return groupInt(n) + " " + i18n.t("common:units.som");
}

/**
 * Grafik o'qi uchun qisqartirilgan son: 1 250 000 → "1.3M", 45 000 → "45k".
 * Faqat O'Q belgilari uchun — ko'pikda va kartalarda to'liq `fmtMoney`.
 */
export function fmtCompact(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${Math.round(v / 1000)}k`;
  return String(Math.round(v));
}

export function fmtInt(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return groupInt(v);
}

/**
 * Miqdor (`Numeric(12,3)`) — kasr qismi bo'lsa saqlanadi, bo'lmasa
 * ko'rsatilmaydi: "3", "2,5", "0,4". `fmtInt` bu yerda YARAMAYDI — u yaxlitlab
 * 2.5 litrni "3" ga, 0.4 kg'ni "0" ga aylantirib yuborardi.
 */
export function fmtQty(v: number | string | null | undefined): string {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  if (Number.isInteger(n)) return groupInt(n);
  const whole = Math.trunc(Math.abs(n));
  const frac = Math.abs(n) - whole;
  // Uchta kasrgacha, ortiqcha nollarsiz. Kasr ajratuvchisi — vergul (o'zbek
  // va rus tilida bir xil).
  const fracStr = frac.toFixed(3).slice(2).replace(/0+$/, "");
  return `${n < 0 ? "-" : ""}${groupInt(whole)},${fracStr}`;
}

/**
 * Backend pul maydonlarini `Numeric(12,2)` — "1000.00" satr sifatida qaytaradi.
 * `MoneyInput` esa faqat xom butun raqam satri bilan ishlaydi (nuqtani raqam
 * deb olib "100000" qiladi). Tahrirlash formasiga berishdan oldin kasr qismini
 * olib tashlaymiz.
 */
export function toMoneyRaw(s?: string | null): string {
  if (!s) return "";
  const n = Number(s);
  return Number.isFinite(n) ? String(Math.trunc(n)) : "";
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseServerDate(iso);
  return new Intl.DateTimeFormat("uz-UZ", {
    timeZone: TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

export function fmtDateLong(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseServerDate(iso);
  if (i18n.resolvedLanguage === "ru") {
    return new Intl.DateTimeFormat("ru-RU", {
      timeZone: TZ,
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  }
  // O'zbekcha: "19-iyun 2026-yil" (Toshkent vaqti bo'yicha).
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  return `${get("day")}-${UZ_MONTHS[get("month") - 1]} ${get("year")}-yil`;
}

/** Qisqa kun-oy yorlig'i (grafik o'qi uchun): "19-iyun" / "19 июн". */
export function fmtDayMon(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  const monthIdx = m ? Number(m[2]) - 1 : new Date(iso).getMonth();
  const day = m ? Number(m[3]) : new Date(iso).getDate();
  if (i18n.resolvedLanguage === "ru") {
    const d = m ? new Date(Number(m[1]), monthIdx, day) : new Date(iso);
    return new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short" }).format(d);
  }
  return `${day}-${UZ_MONTHS[monthIdx]}`;
}

/** Oy-yil yorlig'i (yillik grafik o'qi uchun): "iyun 26" / "июн 26". */
export function fmtMonYear(iso: string | null | undefined): string {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})/.exec(iso);
  const year = m ? Number(m[1]) : new Date(iso).getFullYear();
  const monthIdx = m ? Number(m[2]) - 1 : new Date(iso).getMonth();
  const yy = String(year).slice(-2);
  if (i18n.resolvedLanguage === "ru") {
    return new Intl.DateTimeFormat("ru-RU", { month: "short", year: "2-digit" }).format(
      new Date(year, monthIdx, 1),
    );
  }
  return `${UZ_MONTHS[monthIdx]} ${yy}`;
}

/**
 * To'liq oy-yil sarlavhasi (jurnal oy guruhlari uchun): "Avgust 2026" /
 * "Август 2026". Kalit "YYYY-MM" yoki undan uzun (YYYY-MM-DD) bo'lishi mumkin.
 * Ruscha oy alohida formatlanadi (nominativ "август") — oy+yil birga so'ralsa
 * Intl " г." qo'shimchasini qo'shib yuboradi.
 */
export function fmtMonthYear(key: string | null | undefined): string {
  if (!key) return "";
  const m = /^(\d{4})-(\d{2})/.exec(key);
  if (!m) return key;
  const year = m[1];
  const monthIdx = Number(m[2]) - 1;
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
  if (i18n.resolvedLanguage === "ru") {
    const mon = new Intl.DateTimeFormat("ru-RU", { month: "long" }).format(
      new Date(Number(year), monthIdx, 1),
    );
    return `${cap(mon)} ${year}`;
  }
  return `${cap(UZ_MONTHS[monthIdx])} ${year}`;
}

/**
 * Soat bucket yorlig'i: "2026-08-12T14:00" → "14:00". Backend soat kalitini
 * ALLAQACHON mahalliy vaqtda (Asia/Tashkent) yozadi, shuning uchun bu yerda
 * qayta siljitmaymiz — aks holda vaqt ikki marta ko'chirilardi.
 */
export function fmtHour(key: string | null | undefined): string {
  if (!key) return "";
  const m = /T(\d{2}):(\d{2})/.exec(key);
  return m ? `${m[1]}:${m[2]}` : key;
}

/** Bucket kalitini uning darajasiga mos yorliqqa aylantiradi. */
export function fmtBucketLabel(
  key: string | null | undefined,
  bucket: "hour" | "day" | "week" | "month",
): string {
  if (!key) return "";
  if (bucket === "hour") return fmtHour(key);
  if (bucket === "month") return fmtMonYear(key);
  // Hafta bucket'i dushanba sanasi bilan keladi — kun-oy yorlig'i yetarli.
  return fmtDayMon(key);
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = parseServerDate(iso);
  return new Intl.DateTimeFormat("uz-UZ", {
    timeZone: TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}
